import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader, KpiCard, Panel, EmptyState, formatEur } from "@/components/shared/PageUi";
import { toast } from "sonner";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import {
  Briefcase, Calendar, Plus, Pencil, Trash2, Search, Clock, MapPin,
  Users, Route as RouteIcon, Tag, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from "lucide-react";

const SHIFT_TYPES = [
  { value: "regular", label: "Säännöllinen", color: "text-white/70" },
  { value: "overtime", label: "Ylityö", color: "text-amber-300" },
  { value: "sunday", label: "Sunnuntai (+100%)", color: "text-purple-300" },
  { value: "evening", label: "Ilta (+15-20%)", color: "text-blue-300" },
  { value: "night", label: "Yö (+30-50%)", color: "text-indigo-300" },
  { value: "holiday", label: "Pyhä", color: "text-red-300" },
];
const SHIFT_STATUS = [
  { value: "scheduled", label: "Suunniteltu", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  { value: "in_progress", label: "Käynnissä", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "completed", label: "Suoritettu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { value: "approved", label: "Hyväksytty", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  { value: "cancelled", label: "Peruutettu", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
];
const GIG_STATUS = [
  { value: "open", label: "Avoinna", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "assigned", label: "Annettu", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  { value: "in_progress", label: "Käynnissä", cls: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  { value: "completed", label: "Suoritettu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { value: "cancelled", label: "Peruutettu", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
];

const todayIso = () => new Date().toISOString();
const dateOnly = (iso) => (iso || "").slice(0, 10);
const timeOnly = (iso) => (iso || "").slice(11, 16);
const mondayOf = (d) => { const x = new Date(d); const day = x.getDay() || 7; if (day !== 1) x.setHours(-24 * (day - 1)); x.setHours(0, 0, 0, 0); return x; };

const defaultShift = () => ({
  employee_id: "", job_id: "", gig_id: "", route_id: "", title: "",
  start_at: new Date().toISOString().slice(0, 16),
  end_at: new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 16),
  break_minutes: 30, location: "", notes: "",
  status: "scheduled", shift_type: "regular", hourly_rate_override: null,
});
const defaultGig = () => ({
  title: "", customer: "", customer_phone: "", location: "", description: "",
  start_at: new Date().toISOString().slice(0, 16),
  end_at: new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 16),
  base_price: 0, hourly_rate: 0, payment_type: "fixed",
  status: "open", assigned_employee_id: "", notes: "",
});
const defaultJob = () => ({ name: "", description: "", default_hourly_rate: 0, color: "#a78bfa", is_active: true });
const defaultRoute = () => ({ name: "", description: "", start_location: "", end_location: "", estimated_duration_hours: 0, distance_km: 0, stops: [], color: "#34d399", is_active: true });

export default function ShiftsDashboard() {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));

  const [shiftDialog, setShiftDialog] = useState({ open: false, mode: "create", data: defaultShift() });
  const [gigDialog, setGigDialog] = useState({ open: false, mode: "create", data: defaultGig() });
  const [jobDialog, setJobDialog] = useState({ open: false, mode: "create", data: defaultJob() });
  const [routeDialog, setRouteDialog] = useState({ open: false, mode: "create", data: defaultRoute() });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, ws, j, r, g, e] = await Promise.all([
        api.get("/shifts/stats"),
        api.get("/shifts/"),
        api.get("/shifts/jobs"),
        api.get("/shifts/routes"),
        api.get("/shifts/gigs"),
        api.get("/payroll/employees"),
      ]);
      setStats(s.data);
      setShifts(ws.data || []);
      setJobs(j.data || []);
      setRoutes(r.data || []);
      setGigs(g.data || []);
      setEmployees(e.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // -- CRUD generic
  const save = async (endpoint, dialog, setDialog, defaults) => {
    const d = dialog.data;
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api.post(endpoint, d);
      } else {
        await api.put(`${endpoint}/${d.id}`, d);
      }
      toast.success("Tallennettu");
      setDialog({ open: false, mode: "create", data: defaults() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const del = async (endpoint, id, label) => {
    if (!window.confirm(`Poista ${label}?`)) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "—";
  };
  const jobName = (id) => jobs.find(x => x.id === id)?.name || "";
  const routeName = (id) => routes.find(x => x.id === id)?.name || "";

  // -- Week view
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const shiftsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach(d => { map[d.toISOString().slice(0, 10)] = []; });
    shifts.forEach(s => {
      const k = dateOnly(s.start_at);
      if (map[k]) map[k].push(s);
    });
    return map;
  }, [shifts, weekDays]);

  return (
    <div className="space-y-6" data-testid="shifts-dashboard">
      <PageHeader
        kicker="Sovellus • Työvuoroaikataulu"
        title="Työvuorot & Keikat"
        description="Hallinnoi työvuoroja, keikkoja, työnimikkeitä ja reittejä. Synkronoituu palkanlaskentaan automaattisesti."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShiftDialog({ open: true, mode: "create", data: defaultShift() })} className="btn-luxe inline-flex items-center gap-2" data-testid="shift-new">
              <Plus className="w-4 h-4" /> Uusi vuoro
            </Button>
            <Button variant="outline" onClick={() => setGigDialog({ open: true, mode: "create", data: defaultGig() })} className="border-amber-400/30" data-testid="gig-new">
              <Tag className="w-4 h-4 mr-1" /> Uusi keikka
            </Button>
            <Button variant="outline" onClick={() => setJobDialog({ open: true, mode: "create", data: defaultJob() })} className="border-white/10" data-testid="job-new">
              <Briefcase className="w-4 h-4 mr-1" /> Työnimike
            </Button>
            <Button variant="outline" onClick={() => setRouteDialog({ open: true, mode: "create", data: defaultRoute() })} className="border-white/10" data-testid="route-new">
              <RouteIcon className="w-4 h-4 mr-1" /> Reitti
            </Button>
          </div>
        }
      />

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Clock className="w-4 h-4" />} label="Tämän viikon vuorot" value={stats?.week_shifts ?? "—"} testid="kpi-week-shifts" loading={loading} />
        <KpiCard icon={<Calendar className="w-4 h-4" />} label="Tunnit tällä viikolla" value={`${stats?.week_completed_hours ?? 0} h`} accent="text-emerald-300" testid="kpi-week-hours" loading={loading} />
        <KpiCard icon={<Tag className="w-4 h-4" />} label="Avoimet keikat" value={stats?.open_gigs ?? "—"} accent="text-amber-300" testid="kpi-open-gigs" loading={loading} />
        <KpiCard icon={<RouteIcon className="w-4 h-4" />} label="Reittejä" value={stats?.total_routes ?? "—"} accent="text-blue-300" testid="kpi-routes" loading={loading} />
      </div>

      <Tabs defaultValue="schedule" className="space-y-4">
        <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="schedule" data-testid="tab-schedule">Viikkoaikataulu</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="tab-shifts">Kaikki vuorot</TabsTrigger>
          <TabsTrigger value="gigs" data-testid="tab-gigs">Keikat</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Työnimikkeet</TabsTrigger>
          <TabsTrigger value="routes" data-testid="tab-routes">Reitit</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>

        {/* WEEK SCHEDULE */}
        <TabsContent value="schedule">
          <Panel
            title={
              <div className="flex items-center gap-3">
                <span>Viikko {weekStart.toISOString().slice(0, 10)} → {weekDays[6].toISOString().slice(0, 10)}</span>
              </div>
            }
            action={
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 28); setWeekStart(mondayOf(d)); }}><ChevronsLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(mondayOf(d)); }} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} className="border-amber-400/30 text-xs h-8" data-testid="week-today">Tämä viikko</Button>
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(mondayOf(d)); }} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 28); setWeekStart(mondayOf(d)); }}><ChevronsRight className="w-4 h-4" /></Button>
              </div>
            }
          >
            <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
              {weekDays.map((day, idx) => {
                const key = day.toISOString().slice(0, 10);
                const dayShifts = shiftsByDay[key] || [];
                const dayName = ["Ma", "Ti", "Ke", "To", "Pe", "La", "Su"][idx];
                const isToday = key === new Date().toISOString().slice(0, 10);
                return (
                  <div key={key} className={`rounded-xl border ${isToday ? "border-amber-400/40 bg-amber-500/5" : "border-white/5 bg-white/[0.02]"} p-2 min-h-[120px]`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{dayName}</span>
                      <span className="text-sm text-white">{day.getDate()}.{day.getMonth() + 1}.</span>
                    </div>
                    <div className="space-y-1">
                      {dayShifts.map(s => {
                        const st = SHIFT_TYPES.find(t => t.value === s.shift_type) || SHIFT_TYPES[0];
                        return (
                          <button
                            key={s.id}
                            onClick={() => setShiftDialog({ open: true, mode: "edit", data: { ...s, start_at: s.start_at.slice(0, 16), end_at: s.end_at.slice(0, 16) } })}
                            className={`w-full text-left text-[11px] rounded p-1.5 bg-white/5 hover:bg-white/10 transition ${st.color} truncate block`}
                            data-testid={`week-shift-${s.id}`}
                          >
                            <div className="font-mono">{timeOnly(s.start_at)}–{timeOnly(s.end_at)}</div>
                            <div className="text-white/90 truncate">{empName(s.employee_id)}</div>
                            <div className="text-[10px] text-white/40 truncate">{s.title || jobName(s.job_id) || s.location || "—"}</div>
                          </button>
                        );
                      })}
                      {dayShifts.length === 0 && (
                        <p className="text-[10px] text-white/30 italic text-center py-2">—</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </TabsContent>

        {/* ALL SHIFTS */}
        <TabsContent value="shifts">
          <Panel title={`Kaikki vuorot (${shifts.length})`}>
            {shifts.length === 0 ? (
              <EmptyState icon={<Clock className="w-8 h-8" />} message="Ei työvuoroja vielä" action={<Button onClick={() => setShiftDialog({ open: true, mode: "create", data: defaultShift() })} className="btn-luxe">Luo ensimmäinen</Button>} />
            ) : (
              <div className="overflow-x-auto" data-testid="shifts-table">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-white/40">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2">Päivä</th>
                      <th className="text-left py-2 px-2">Aika</th>
                      <th className="text-left py-2 px-2">Työntekijä</th>
                      <th className="text-left py-2 px-2">Otsikko / Työnimike</th>
                      <th className="text-left py-2 px-2">Tyyppi</th>
                      <th className="text-right py-2 px-2">Tunnit</th>
                      <th className="text-center py-2 px-2">Tila</th>
                      <th className="text-right py-2 px-2">Toiminnot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {shifts.map(s => {
                      const st = SHIFT_STATUS.find(x => x.value === s.status) || SHIFT_STATUS[0];
                      const sty = SHIFT_TYPES.find(x => x.value === s.shift_type) || SHIFT_TYPES[0];
                      return (
                        <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`shift-row-${s.id}`}>
                          <td className="py-2 px-2 text-white/70">{dateOnly(s.start_at)}</td>
                          <td className="py-2 px-2 font-mono text-xs">{timeOnly(s.start_at)} – {timeOnly(s.end_at)}</td>
                          <td className="py-2 px-2 text-white">{empName(s.employee_id)}</td>
                          <td className="py-2 px-2 text-white/80">{s.title || jobName(s.job_id) || routeName(s.route_id) || "—"}</td>
                          <td className={`py-2 px-2 ${sty.color}`}>{sty.label}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-300">{s.hours} h</td>
                          <td className="py-2 px-2 text-center"><Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge></td>
                          <td className="py-2 px-2 text-right space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => setShiftDialog({ open: true, mode: "edit", data: { ...s, start_at: s.start_at.slice(0, 16), end_at: s.end_at.slice(0, 16) } })}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => del("/shifts", s.id, "työvuoro")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* GIGS */}
        <TabsContent value="gigs">
          <Panel title={`Keikat (${gigs.length})`}>
            {gigs.length === 0 ? (
              <EmptyState icon={<Tag className="w-8 h-8" />} message="Ei keikkoja vielä" action={<Button onClick={() => setGigDialog({ open: true, mode: "create", data: defaultGig() })} className="btn-luxe">Luo ensimmäinen</Button>} />
            ) : (
              <table className="w-full text-sm" data-testid="gigs-table">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Otsikko</th>
                    <th className="text-left py-2 px-2">Asiakas</th>
                    <th className="text-left py-2 px-2">Aika</th>
                    <th className="text-left py-2 px-2">Sijainti</th>
                    <th className="text-right py-2 px-2">Hinta</th>
                    <th className="text-center py-2 px-2">Tila</th>
                    <th className="text-right py-2 px-2">Toiminnot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {gigs.map(g => {
                    const st = GIG_STATUS.find(x => x.value === g.status) || GIG_STATUS[0];
                    return (
                      <tr key={g.id} className="hover:bg-white/[0.02]" data-testid={`gig-row-${g.id}`}>
                        <td className="py-2 px-2 text-white font-medium">{g.title}</td>
                        <td className="py-2 px-2 text-white/70">{g.customer || "—"}</td>
                        <td className="py-2 px-2 font-mono text-xs text-white/60">{dateOnly(g.start_at)} {timeOnly(g.start_at)}</td>
                        <td className="py-2 px-2 text-white/70">{g.location || "—"}</td>
                        <td className="py-2 px-2 text-right font-mono">{g.payment_type === "hourly" ? `${formatEur(g.hourly_rate)}/h` : formatEur(g.base_price)}</td>
                        <td className="py-2 px-2 text-center"><Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge></td>
                        <td className="py-2 px-2 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => setGigDialog({ open: true, mode: "edit", data: { ...g, start_at: g.start_at.slice(0, 16), end_at: g.end_at.slice(0, 16) } })}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => del("/shifts/gigs", g.id, "keikka")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>
        </TabsContent>

        {/* JOBS */}
        <TabsContent value="jobs">
          <Panel title={`Työnimikkeet (${jobs.length})`}>
            {jobs.length === 0 ? (
              <EmptyState icon={<Briefcase className="w-8 h-8" />} message="Ei työnimikkeitä – luo esim. 'Varastotyö' tai 'Asiakaspalvelu'" action={<Button onClick={() => setJobDialog({ open: true, mode: "create", data: defaultJob() })} className="btn-luxe">Luo ensimmäinen</Button>} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="jobs-grid">
                {jobs.map(j => (
                  <div key={j.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04]" data-testid={`job-card-${j.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ background: j.color || "#a78bfa" }} />
                        <p className="text-white font-medium">{j.name}</p>
                      </div>
                      <div className="space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setJobDialog({ open: true, mode: "edit", data: j })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del("/shifts/jobs", j.id, "työnimike")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                    {j.description && <p className="text-xs text-white/50 mt-2">{j.description}</p>}
                    {j.default_hourly_rate > 0 && (
                      <p className="text-xs text-amber-200 mt-2">Oletustuntipalkka: {formatEur(j.default_hourly_rate)} / h</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* ROUTES */}
        <TabsContent value="routes">
          <Panel title={`Reitit (${routes.length})`}>
            {routes.length === 0 ? (
              <EmptyState icon={<RouteIcon className="w-8 h-8" />} message="Ei reittejä – luo esim. 'Helsinki – Tampere'" action={<Button onClick={() => setRouteDialog({ open: true, mode: "create", data: defaultRoute() })} className="btn-luxe">Luo ensimmäinen</Button>} />
            ) : (
              <div className="space-y-2" data-testid="routes-list">
                {routes.map(r => (
                  <div key={r.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`route-card-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ background: r.color || "#34d399" }} />
                          <p className="text-white font-medium">{r.name}</p>
                          {r.distance_km > 0 && <span className="text-xs text-white/40">{r.distance_km} km</span>}
                          {r.estimated_duration_hours > 0 && <span className="text-xs text-amber-200">{r.estimated_duration_hours} h</span>}
                        </div>
                        <p className="text-xs text-white/50 mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />{r.start_location || "—"} → {r.end_location || "—"}
                        </p>
                        {(r.stops || []).length > 0 && (
                          <p className="text-[11px] text-white/40 mt-2">
                            Pysähdykset: {(r.stops || []).map(s => s.name).join(" → ")}
                          </p>
                        )}
                      </div>
                      <div className="space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setRouteDialog({ open: true, mode: "edit", data: r })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del("/shifts/routes", r.id, "reitti")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="staff">
          <ModuleStaffTab moduleKey="shifts" moduleLabel="Työvuorot & Keikat" />
        </TabsContent>
      </Tabs>

      {/* Shift Dialog */}
      <Dialog open={shiftDialog.open} onOpenChange={v => !v && setShiftDialog({ open: false, mode: "create", data: defaultShift() })}>
        <DialogContent className="max-w-2xl" data-testid="shift-dialog">
          <DialogHeader><DialogTitle>{shiftDialog.mode === "create" ? "Uusi työvuoro" : "Muokkaa työvuoroa"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Työntekijä *">
                <select value={shiftDialog.data.employee_id} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, employee_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="shift-employee">
                  <option value="">— valitse —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </Field>
              <Field label="Otsikko (vapaaehtoinen)">
                <Input value={shiftDialog.data.title} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, title: e.target.value } })} placeholder="esim. Asiakaspalvelu" data-testid="shift-title" />
              </Field>
              <Field label="Työnimike">
                <select value={shiftDialog.data.job_id} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, job_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
              </Field>
              <Field label="Reitti">
                <select value={shiftDialog.data.route_id} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, route_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>
              <Field label="Keikka">
                <select value={shiftDialog.data.gig_id} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, gig_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {gigs.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              </Field>
              <Field label="Vuoron tyyppi *">
                <select value={shiftDialog.data.shift_type} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, shift_type: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="shift-type">
                  {SHIFT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Alkaa *"><Input type="datetime-local" value={shiftDialog.data.start_at} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, start_at: e.target.value } })} data-testid="shift-start" /></Field>
              <Field label="Päättyy *"><Input type="datetime-local" value={shiftDialog.data.end_at} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, end_at: e.target.value } })} data-testid="shift-end" /></Field>
              <Field label="Tauot (min)"><Input type="number" value={shiftDialog.data.break_minutes} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, break_minutes: parseFloat(e.target.value) || 0 } })} /></Field>
              <Field label="Tuntipalkka (yliajo, €/h)"><Input type="number" step="0.01" value={shiftDialog.data.hourly_rate_override ?? ""} placeholder="auto" onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, hourly_rate_override: e.target.value === "" ? null : parseFloat(e.target.value) } })} /></Field>
              <Field label="Sijainti"><Input value={shiftDialog.data.location} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, location: e.target.value } })} /></Field>
              <Field label="Tila">
                <select value={shiftDialog.data.status} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, status: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {SHIFT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Muistiinpanot"><textarea value={shiftDialog.data.notes || ""} onChange={e => setShiftDialog({ ...shiftDialog, data: { ...shiftDialog.data, notes: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShiftDialog({ open: false, mode: "create", data: defaultShift() })}>Peruuta</Button>
            <Button onClick={() => save("/shifts", shiftDialog, setShiftDialog, defaultShift)} disabled={saving} className="btn-luxe" data-testid="shift-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gig Dialog */}
      <Dialog open={gigDialog.open} onOpenChange={v => !v && setGigDialog({ open: false, mode: "create", data: defaultGig() })}>
        <DialogContent className="max-w-2xl" data-testid="gig-dialog">
          <DialogHeader><DialogTitle>{gigDialog.mode === "create" ? "Uusi keikka" : "Muokkaa keikkaa"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Otsikko *"><Input value={gigDialog.data.title} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, title: e.target.value } })} data-testid="gig-title" /></Field>
              <Field label="Asiakas"><Input value={gigDialog.data.customer} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, customer: e.target.value } })} data-testid="gig-customer" /></Field>
              <Field label="Asiakkaan puhelin"><Input value={gigDialog.data.customer_phone} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, customer_phone: e.target.value } })} /></Field>
              <Field label="Sijainti"><Input value={gigDialog.data.location} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, location: e.target.value } })} /></Field>
              <Field label="Alkaa"><Input type="datetime-local" value={gigDialog.data.start_at} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, start_at: e.target.value } })} data-testid="gig-start" /></Field>
              <Field label="Päättyy"><Input type="datetime-local" value={gigDialog.data.end_at} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, end_at: e.target.value } })} data-testid="gig-end" /></Field>
              <Field label="Hinnoittelu">
                <select value={gigDialog.data.payment_type} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, payment_type: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="fixed">Kiinteä hinta</option>
                  <option value="hourly">Tunti</option>
                  <option value="commission">Provisio</option>
                </select>
              </Field>
              {gigDialog.data.payment_type === "hourly" ? (
                <Field label="Tuntihinta (€/h)"><Input type="number" step="0.01" value={gigDialog.data.hourly_rate} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, hourly_rate: parseFloat(e.target.value) || 0 } })} /></Field>
              ) : (
                <Field label="Kiinteä hinta (€)"><Input type="number" step="0.01" value={gigDialog.data.base_price} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, base_price: parseFloat(e.target.value) || 0 } })} /></Field>
              )}
              <Field label="Annettu työntekijälle">
                <select value={gigDialog.data.assigned_employee_id || ""} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, assigned_employee_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">— ei vielä —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </Field>
              <Field label="Tila">
                <select value={gigDialog.data.status} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, status: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {GIG_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Kuvaus / muistiinpanot"><textarea value={gigDialog.data.description || ""} onChange={e => setGigDialog({ ...gigDialog, data: { ...gigDialog.data, description: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGigDialog({ open: false, mode: "create", data: defaultGig() })}>Peruuta</Button>
            <Button onClick={() => save("/shifts/gigs", gigDialog, setGigDialog, defaultGig)} disabled={saving} className="btn-luxe" data-testid="gig-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Dialog */}
      <Dialog open={jobDialog.open} onOpenChange={v => !v && setJobDialog({ open: false, mode: "create", data: defaultJob() })}>
        <DialogContent className="max-w-md" data-testid="job-dialog">
          <DialogHeader><DialogTitle>{jobDialog.mode === "create" ? "Uusi työnimike" : "Muokkaa työnimikettä"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Nimi *"><Input value={jobDialog.data.name} onChange={e => setJobDialog({ ...jobDialog, data: { ...jobDialog.data, name: e.target.value } })} placeholder="esim. Varastotyöntekijä" data-testid="job-name" /></Field>
            <Field label="Kuvaus"><textarea value={jobDialog.data.description || ""} onChange={e => setJobDialog({ ...jobDialog, data: { ...jobDialog.data, description: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" /></Field>
            <Field label="Oletustuntipalkka (€/h)"><Input type="number" step="0.01" value={jobDialog.data.default_hourly_rate} onChange={e => setJobDialog({ ...jobDialog, data: { ...jobDialog.data, default_hourly_rate: parseFloat(e.target.value) || 0 } })} data-testid="job-rate" /></Field>
            <Field label="Väri"><Input type="color" value={jobDialog.data.color} onChange={e => setJobDialog({ ...jobDialog, data: { ...jobDialog.data, color: e.target.value } })} className="h-10" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setJobDialog({ open: false, mode: "create", data: defaultJob() })}>Peruuta</Button>
            <Button onClick={() => save("/shifts/jobs", jobDialog, setJobDialog, defaultJob)} disabled={saving} className="btn-luxe" data-testid="job-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Dialog */}
      <Dialog open={routeDialog.open} onOpenChange={v => !v && setRouteDialog({ open: false, mode: "create", data: defaultRoute() })}>
        <DialogContent className="max-w-xl" data-testid="route-dialog">
          <DialogHeader><DialogTitle>{routeDialog.mode === "create" ? "Uusi reitti" : "Muokkaa reittiä"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Reitin nimi *"><Input value={routeDialog.data.name} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, name: e.target.value } })} placeholder="esim. Helsinki – Tampere" data-testid="route-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Lähtöpaikka"><Input value={routeDialog.data.start_location} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, start_location: e.target.value } })} /></Field>
              <Field label="Saapumispaikka"><Input value={routeDialog.data.end_location} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, end_location: e.target.value } })} /></Field>
              <Field label="Kesto-arvio (h)"><Input type="number" step="0.5" value={routeDialog.data.estimated_duration_hours} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, estimated_duration_hours: parseFloat(e.target.value) || 0 } })} /></Field>
              <Field label="Matka (km)"><Input type="number" value={routeDialog.data.distance_km} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, distance_km: parseFloat(e.target.value) || 0 } })} /></Field>
            </div>
            <div>
              <Label className="text-xs text-white/60">Pysähdykset</Label>
              <div className="mt-2 space-y-2">
                {(routeDialog.data.stops || []).map((stop, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr,100px,auto] gap-2 items-center">
                    <Input value={stop.name} onChange={e => {
                      const stops = [...(routeDialog.data.stops || [])];
                      stops[idx] = { ...stops[idx], name: e.target.value };
                      setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, stops } });
                    }} placeholder="Pysähdyksen nimi" />
                    <Input value={stop.arrive_at || ""} onChange={e => {
                      const stops = [...(routeDialog.data.stops || [])];
                      stops[idx] = { ...stops[idx], arrive_at: e.target.value };
                      setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, stops } });
                    }} placeholder="HH:MM" />
                    <Button size="sm" variant="ghost" onClick={() => {
                      const stops = (routeDialog.data.stops || []).filter((_, i) => i !== idx);
                      setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, stops } });
                    }} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, stops: [...(routeDialog.data.stops || []), { name: "", arrive_at: "" }] } })} className="border-amber-400/30"><Plus className="w-3.5 h-3.5 mr-1" /> Lisää pysähdys</Button>
              </div>
            </div>
            <Field label="Kuvaus"><textarea value={routeDialog.data.description || ""} onChange={e => setRouteDialog({ ...routeDialog, data: { ...routeDialog.data, description: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRouteDialog({ open: false, mode: "create", data: defaultRoute() })}>Peruuta</Button>
            <Button onClick={() => save("/shifts/routes", routeDialog, setRouteDialog, defaultRoute)} disabled={saving} className="btn-luxe" data-testid="route-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
